<template>
  <div class="navigation-title-section">
    <div class="navigation-title-section-content">Pinned Artworks</div>
  </div>
  <div class="pinned-page">
    <p class="intro-text">
      Pinned artworks appear first on Collect Ceres without filters.
      Search to add artworks, drag to reorder, and use Save or Active/Inactive to manage pinned order.
    </p>
    <div class="top-row">
      <el-select
        v-model="pendingArtwork"
        class="search-select"
        filterable
        remote
        clearable
        reserve-keyword
        placeholder="Search artwork by title to add..."
        :remote-method="remoteArtworkSearch"
        :loading="searchLoading"
        value-key="id"
        @change="onArtworkPicked"
        @visible-change="onDropdownVisibleChange"
      >
        <el-option
          v-for="item in artworkOptions"
          :key="item.id"
          :label="artworkSelectLabel(item)"
          :value="item"
        >
          <div class="dropdown-option-row">
            <img
              v-if="item.featured_image && item.featured_image.slug"
              :src="previewDomain + item.featured_image.slug"
              class="dropdown-option-thumb"
            />
            <div v-else class="dropdown-option-thumb-placeholder" />
            <span class="dropdown-option-label">{{ artworkSelectLabel(item) }}</span>
          </div>
        </el-option>
      </el-select>
      <el-button type="primary" :loading="saving" @click="save">
        Save
      </el-button>
      <div class="order-toggle">
        <el-button-group>
          <el-button
            :type="pinningEnabled ? 'primary' : ''"
            :loading="togglingPinning && pinningEnabled"
            @click="onPinningToggle(true)"
          >Active</el-button>
          <el-button
            :type="!pinningEnabled ? 'primary' : ''"
            :loading="togglingPinning && !pinningEnabled"
            @click="onPinningToggle(false)"
          >Inactive</el-button>
        </el-button-group>
      </div>
    </div>

    <div class="mt-3">
      <p v-if="pinnedArtworks.length === 0" class="empty-notice">
        No artworks pinned yet. Search and add artworks above.
      </p>

      <draggable
        v-else
        :list="pinnedArtworks"
        item-key="id"
        class="artwork-list"
        ghost-class="ghost-row"
        handle=".drag-handle"
      >
        <template #item="{ element: row, index: i }">
          <div class="artwork-row">
            <span class="position-num">{{ i + 1 }}</span>
            <span class="drag-handle" title="Drag to reorder">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="14" height="14" fill="currentColor">
                <path d="M40 352l48-48c9.4-9.4 24.6-9.4 33.9 0l48 48c9.4 9.4 9.4 24.6 0 33.9l-48 48c-9.4 9.4-24.6 9.4-33.9 0l-48-48c-9.4-9.4-9.4-24.6 0-33.9zm192-224l-48 48c-9.4 9.4-24.6 9.4-33.9 0l-48-48c-9.4-9.4-9.4-24.6 0-33.9l48-48c9.4-9.4 24.6-9.4 33.9 0l48 48c9.4 9.4 9.4 24.6 0 33.9zM40 160l48 48c9.4 9.4 24.6 9.4 33.9 0l48-48c9.4-9.4 9.4-24.6 0-33.9l-48-48c-9.4-9.4-24.6-9.4-33.9 0l-48 48c-9.4 9.4-9.4 24.6 0 33.9zm192 192l-48-48c-9.4-9.4-24.6-9.4-33.9 0l-48 48c-9.4 9.4-9.4 24.6 0 33.9l48 48c9.4 9.4 24.6 9.4 33.9 0l48-48c9.4-9.4 9.4-24.6 0-33.9z"/>
              </svg>
            </span>
            <img
              v-if="row.image_slug"
              :src="previewDomain + row.image_slug"
              class="artwork-thumb"
            />
            <div v-else class="artwork-thumb-placeholder" />
            <span class="artwork-label">{{ row.title }}<span v-if="row.artist_name"> — {{ row.artist_name }}</span></span>
            <span class="remove-btn" title="Remove" @click="pinnedArtworks.splice(i, 1)">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="12" height="12" fill="white">
                <path d="M32 464a48 48 0 0 0 48 48h288a48 48 0 0 0 48-48V128H32zm272-256a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zM432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16z"/>
              </svg>
            </span>
          </div>
        </template>
      </draggable>
    </div>

  </div>
</template>

<script>
import draggable from "vuedraggable";
import { ElNotification } from "element-plus";

export default {
  name: "PinnedArtworks",
  components: { draggable },
  data() {
    return {
      pinnedArtworks: [],
      pendingArtwork: null,
      artworkOptions: [],
      searchLoading: false,
      saving: false,
      lastQuery: "",
      pinningEnabled: true,
      togglingPinning: false,
    };
  },
  computed: {
    previewDomain() {
      return process.env.VUE_APP_SHARED_API;
    },
  },
  created() {
    this.loadPinned();
    this.fetchArtworks("");
    this.loadPinningEnabled();
  },
  methods: {
    async loadPinned() {
      try {
        const res = await this.$api.get(
          `${process.env.VUE_APP_SHARED_API}api/artworks/pinned`
        );
        this.pinnedArtworks = res.data || [];
      } catch (_) {
        ElNotification({ title: "Error", message: "Could not load pinned artworks.", type: "error" });
      }
    },
    artworkSelectLabel(item) {
      return item.artist?.name
        ? `${item.title} — ${item.artist.name}`
        : item.title;
    },
    onArtworkPicked(art) {
      if (!art || !art.id) {
        this.pendingArtwork = null;
        return;
      }
      if (this.pinnedArtworks.some((a) => a.id === art.id)) {
        this.pendingArtwork = null;
        return;
      }
      this.pinnedArtworks.push({
        id: art.id,
        title: art.title || "",
        slug: art.slug || "",
        artist_name: art.artist?.name || "",
        image_slug: art.featured_image?.slug || "",
      });
      this.pendingArtwork = null;
    },
    onDropdownVisibleChange(visible) {
      if (visible && !this.searchLoading && (this.artworkOptions.length === 0 || this.lastQuery !== "")) {
        this.fetchArtworks("");
      }
    },
    remoteArtworkSearch(query) {
      this.fetchArtworks(query);
    },
    fetchArtworks(query) {
      this.lastQuery = query;
      this.searchLoading = true;
      this.artworkOptions = [];
      const base = `${process.env.VUE_APP_SHARED_API}api/artworks/all`;
      const url = query ? `${base}?search=${encodeURIComponent(query.toLowerCase())}` : base;
      this.$api
        .get(url)
        .then((res) => {
          this.artworkOptions = res.data || [];
          this.searchLoading = false;
        })
        .catch(() => {
          this.searchLoading = false;
        });
    },
    async loadPinningEnabled() {
      try {
        const res = await this.$api.get(
          `${process.env.VUE_APP_SHARED_API}api/artworks/pinning-enabled`
        );
        this.pinningEnabled = res.data.enabled;
      } catch (_) {}
    },
    async onPinningToggle(value) {
      if (this.pinningEnabled === value) return;
      this.pinningEnabled = value;
      this.togglingPinning = true;
      try {
        await this.$api.post(
          `${process.env.VUE_APP_SHARED_API}api/artworks/pinning-enabled`,
          { enabled: value }
        );
        ElNotification({
          title: value ? "Order enabled" : "Order disabled",
          message: value ? "Pinned artworks will appear first." : "Pinned ordering is now off.",
          type: "success",
        });
      } catch (_) {
        this.pinningEnabled = !value;
        ElNotification({ title: "Error", message: "Could not update setting.", type: "error" });
      } finally {
        this.togglingPinning = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        const ids = this.pinnedArtworks.map((a) => a.id);
        await this.$api.post(
          `${process.env.VUE_APP_SHARED_API}api/artworks/pinned`,
          { artworks: ids }
        );
        ElNotification({ title: "Saved!", message: `${ids.length} artwork(s) pinned.`, type: "success" });
      } catch (_) {
        ElNotification({ title: "Error", message: "Could not save pinned artworks.", type: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pinned-page {
  padding: 16px;
}
.intro-text {
  color: #555;
  margin-bottom: 16px;
}
.mt-3 {
  margin-top: 16px;
}
.empty-notice {
  color: #888;
}

/* Dropdown option with thumbnail */
.dropdown-option-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
}
.dropdown-option-thumb {
  width: 36px;
  height: 36px;
  object-fit: contain;
  flex-shrink: 0;
  background: #f5f5f5;
}
.dropdown-option-thumb-placeholder {
  width: 36px;
  height: 36px;
  background: #eee;
  flex-shrink: 0;
}
.dropdown-option-label {
  font-size: 13px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Order toggle group */
.order-toggle {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

/* Search + save row */
.top-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}
.search-select {
  flex: 1;
  max-width: 520px;
}

/* Pinned list */
.artwork-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.artwork-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 40px 6px 6px;
  border: 1px solid #e0e0e0;
  position: relative;
  background: #fff;
}
.position-num {
  min-width: 24px;
  font-size: 12px;
  color: #888;
  text-align: center;
  font-weight: 600;
}
.drag-handle {
  flex-shrink: 0;
  cursor: grab;
  color: #909399;
  display: flex;
  align-items: center;
  padding: 4px 6px;
  user-select: none;
}
.drag-handle:active {
  cursor: grabbing;
}
.artwork-thumb {
  width: 52px;
  height: 52px;
  object-fit: contain;
  flex-shrink: 0;
}
.artwork-thumb-placeholder {
  width: 52px;
  height: 52px;
  background: #f0f0f0;
  flex-shrink: 0;
}
.artwork-label {
  flex: 1;
  font-size: 13px;
  min-width: 0;
}
.remove-btn {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  background: #e53e3e;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ghost-row {
  opacity: 0.5;
}
</style>
